import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import './FormMonivaeContact.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const FormMonivaeContact = () => {
  const [submitting, setSubmitting] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    studentFirstName: '',
    studentLastName: '',
    parentGuardianFirstName: '',
    parentGuardianLastName: '',
    whoFillingForm: '',
    whoFillingFormOther: '',
    email: '',
    emailBelongsTo: [],
    phoneNumber: '',
    phoneBelongsTo: [],
    message: '',
    bestTimeToContact: '',
    contactPreference: 'email'
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked 
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/monivae-contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_first_name: formData.studentFirstName,
          student_last_name: formData.studentLastName,
          parent_guardian_first_name: formData.parentGuardianFirstName,
          parent_guardian_last_name: formData.parentGuardianLastName,
          who_filling_form: formData.whoFillingForm,
          who_filling_form_other: formData.whoFillingFormOther,
          email: formData.email,
          email_belongs_to: formData.emailBelongsTo.join(', '),
          phoneNumber: formData.phoneNumber,
          phone_belongs_to: formData.phoneBelongsTo.join(', '),
          message: formData.message,
          bestTimeToContact: formData.bestTimeToContact,
          contactPreference: formData.contactPreference
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            studentFirstName: '',
            studentLastName: '',
            parentGuardianFirstName: '',
            parentGuardianLastName: '',
            whoFillingForm: '',
            whoFillingFormOther: '',
            email: '',
            emailBelongsTo: [],
            phoneNumber: '',
            phoneBelongsTo: [],
            message: '',
            bestTimeToContact: '',
            contactPreference: 'email'
          });
        }, 5000);
      } else {
        setSubmitError(data.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-monivae-contact-standalone">
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img src="/logo.png" alt="Clinical Minds Psychology" className="form-logo" />
          </div>
          <h1 className="form-title">Contact Us - Monivae</h1>
          <p className="form-subtitle">Send us a message and we'll get back to you within 24 hours</p>
        </div>

        <div className="form-content">
          {submitSuccess ? (
            <div className="success-message">
              <CheckCircle size={48} className="success-icon" />
              <h3>Message Sent Successfully!</h3>
              <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {formsOff && <PreviewFormNotice />}
              <div className="privacy-notice">
                <p>We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              </div>

              {/* Student Name */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="studentFirstName">Student First Name *</label>
                  <input
                    type="text"
                    id="studentFirstName"
                    name="studentFirstName"
                    value={formData.studentFirstName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="studentLastName">Student Last Name *</label>
                  <input
                    type="text"
                    id="studentLastName"
                    name="studentLastName"
                    value={formData.studentLastName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Parent/Guardian Name */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentGuardianFirstName">Parent/Guardian First Name (if applicable)</label>
                  <input
                    type="text"
                    id="parentGuardianFirstName"
                    name="parentGuardianFirstName"
                    value={formData.parentGuardianFirstName}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="parentGuardianLastName">Parent/Guardian Last Name (if applicable)</label>
                  <input
                    type="text"
                    id="parentGuardianLastName"
                    name="parentGuardianLastName"
                    value={formData.parentGuardianLastName}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Who is filling out form */}
              <div className="form-group">
                <label htmlFor="whoFillingForm">Who is filling out this form? *</label>
                <select
                  id="whoFillingForm"
                  name="whoFillingForm"
                  value={formData.whoFillingForm}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="I am a student">I am a student</option>
                  <option value="I am the parent/guardian">I am the parent/guardian</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Show text field if "Other" is selected */}
              {formData.whoFillingForm === 'Other' && (
                <div className="form-group">
                  <label htmlFor="whoFillingFormOther">Please specify *</label>
                  <input
                    type="text"
                    id="whoFillingFormOther"
                    name="whoFillingFormOther"
                    value={formData.whoFillingFormOther}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                    placeholder="Please specify who is filling out this form"
                  />
                </div>
              )}

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                />
                <label style={{marginTop: '0.75rem', marginBottom: '0.5rem'}}>Email of:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="emailBelongsTo"
                      value="Student"
                      checked={formData.emailBelongsTo.includes('Student')}
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                    <span>Student</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="emailBelongsTo"
                      value="Parent/Guardian"
                      checked={formData.emailBelongsTo.includes('Parent/Guardian')}
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                    <span>Parent/Guardian</span>
                  </label>
                </div>
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <label style={{marginTop: '0.75rem', marginBottom: '0.5rem'}}>Phone number of:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="phoneBelongsTo"
                      value="Student"
                      checked={formData.phoneBelongsTo.includes('Student')}
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                    <span>Student</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="phoneBelongsTo"
                      value="Parent/Guardian"
                      checked={formData.phoneBelongsTo.includes('Parent/Guardian')}
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                    <span>Parent/Guardian</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contactPreference">Preferred Contact Method *</label>
                <select
                  id="contactPreference"
                  name="contactPreference"
                  value={formData.contactPreference}
                  onChange={handleInputChange}
                  required
                  disabled={submitting}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bestTimeToContact">Best Time to Contact</label>
                <input
                  type="text"
                  id="bestTimeToContact"
                  name="bestTimeToContact"
                  value={formData.bestTimeToContact}
                  onChange={handleInputChange}
                  placeholder="e.g., Weekday mornings, After 6 PM"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={submitting}
                  placeholder="Please tell us how we can help you..."
                ></textarea>
              </div>

              {submitError && (
                <div className="error-message">
                  <p>{submitError}</p>
                </div>
              )}

              <div className="form-footer">
                <p className="consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={submitting || formsOff}
                >
                  {submitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormMonivaeContact;